/**
 * Store coverage notice.
 *
 * The app searches one retailer today. Saying so plainly matters: a shopper
 * seeing only FairPrice results could otherwise assume they are looking at the
 * cheapest option across Singapore, which would be wrong.
 *
 * Each "coming soon" entry names why it is not live yet, rather than implying
 * work is imminent.
 */

import { useState } from 'react';

interface Chain {
  name: string;
  status: 'live' | 'planned';
  /** Shown when expanded — the honest reason, not a roadmap promise. */
  detail: string;
}

const CHAINS: Chain[] = [
  {
    name: 'FairPrice',
    status: 'live',
    detail:
      'Searched from FairPrice’s published product catalog, refreshed daily. Includes barcodes, so nutrition is matched exactly.',
  },
  {
    name: 'Cold Storage',
    status: 'planned',
    detail:
      'Publishes a product index but no prices — its site renders prices in the browser, so there is no catalog to read. Not yet supported.',
  },
  {
    name: 'Giant',
    status: 'planned',
    detail: 'No published product catalog found. Not yet supported.',
  },
  {
    name: 'Sheng Siong',
    status: 'planned',
    detail: 'No published product catalog found. Not yet supported.',
  },
];

export function StoreCoverage(): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const live = CHAINS.filter((c) => c.status === 'live');

  return (
    <div className="notice notice-info" style={{ alignItems: 'center' }}>
      <div style={{ flex: 1 }}>
        <strong>Searching {live.map((c) => c.name).join(', ')}.</strong>{' '}
        Other supermarkets are not included yet, so these results are not a
        Singapore-wide price comparison.{' '}
        <button
          type="button"
          className="btn btn-sm"
          style={{ marginLeft: '0.25rem' }}
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
        >
          {open ? 'Hide' : 'Which stores?'}
        </button>
        {open && (
          <ul style={{ margin: '0.6rem 0 0', paddingLeft: '1.1rem' }}>
            {CHAINS.map((chain) => (
              <li key={chain.name} style={{ marginBottom: '0.35rem' }}>
                <span
                  className={`badge ${chain.status === 'live' ? 'badge-good' : 'badge-neutral'}`}
                  style={{ marginRight: '0.4rem' }}
                >
                  {chain.status === 'live' ? 'Live' : 'Coming soon'}
                </span>
                <strong>{chain.name}</strong>
                <div style={{ color: 'var(--text-muted)', fontSize: '0.78rem' }}>
                  {chain.detail}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

/**
 * Per-outlet pricing notice, for the product page.
 *
 * FairPrice publishes a local-inventory feed with per-outlet prices and offers,
 * but it is not ingested yet. Until it is, the app must not imply that the price
 * shown is outlet-specific.
 */
export function StorePricingNotice(): React.JSX.Element {
  return (
    <p style={{ fontSize: '0.74rem', color: 'var(--text-muted)', margin: '0.5rem 0 0' }}>
      This is the online catalog price. Prices and offers at individual FairPrice
      outlets can differ — per-outlet comparison is not available yet.
    </p>
  );
}
